import React, { useState } from 'react';
import { Card, Button, Table, Badge, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useCourses } from '@hooks';
import { CourseEditorModal } from '@components/admin/CourseEditorModal';
import { useAuth } from '@context';
import { getManagedCourses, isAdmin } from '@utils/permissions';

export function CoursesPage() {
  const { courses, loading, error, createCourse, updateCourse, deleteCourse } = useCourses();
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const scopedCourses = getManagedCourses(user, courses);
  const userIsAdmin = isAdmin(user);

  const [editingCourse, setEditingCourse] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  /**
   * Handle creating a new course
   */
  const handleCreateClick = () => {
    setEditingCourse(null);
    setShowModal(true);
    setActionError(null);
  };

  /**
   * Handle editing an existing course
   */
  const handleEditClick = (course) => {
    setEditingCourse(course);
    setShowModal(true);
    setActionError(null);
  };

  /**
   * Handle saving course (create or update)
   */
  const handleSave = async (formData) => {
    try {
      setActionError(null);
      if (editingCourse) {
        await updateCourse(editingCourse.id, formData);
        setSuccessMessage('Course updated successfully');
      } else {
        await createCourse(formData);
        setSuccessMessage('Course created successfully');
      }
      setShowModal(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setActionError(err.message || 'Failed to save course');
      throw err; // Re-throw to let modal handle it
    }
  };

  /**
   * Handle deleting a course
   */
  const handleDelete = async (course) => {
    if (window.confirm(`Are you sure you want to delete "${course.title}"? This action cannot be undone.`)) {
      try {
        setActionError(null);
        await deleteCourse(course.id);
        setSuccessMessage('Course deleted successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (err) {
        setActionError(err.message || 'Failed to delete course');
      }
    }
  };

  /**
   * Calculate course statistics
   */
  const getCourseStats = (course) => {
    const sections = course.sections || [];
    const sectionCount = sections.length;
    const questionCount = sections.reduce((sum, s) => sum + ((s.questions || []).length), 0);
    const contentCount = sections.reduce((sum, s) => sum + ((s.examples || []).length), 0);

    return { sectionCount, questionCount, contentCount };
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">📚 Course Management</h4>
          <small className="text-muted">Create and manage courses, modules, content, and questions.</small>
        </div>
        {userIsAdmin && (
          <Button variant="primary" onClick={handleCreateClick}>
            + New Course
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setActionError(null)}>
          {error}
        </Alert>
      )}

      {actionError && (
        <Alert variant="danger" dismissible onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {scopedCourses.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <div style={{ fontSize: '3rem' }} className="mb-3">📚</div>
              <h5>No courses yet</h5>
              <p className="mb-3">
                {userIsAdmin
                  ? 'Get started by creating your first course.'
                  : 'No assigned courses are available yet.'}
              </p>
              {userIsAdmin && (
                <Button variant="primary" onClick={handleCreateClick}>
                  + Create Your First Course
                </Button>
              )}
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th className="text-center">Modules</th>
                  <th className="text-center">Content</th>
                  <th className="text-center">Questions</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {scopedCourses.map(course => {
                  const stats = getCourseStats(course);
                  return (
                    <tr key={course.id}>
                      <td>
                        <strong>{course.title}</strong>
                      </td>
                      <td>
                        <div className="text-muted small" style={{ maxWidth: 300 }}>
                          {course.description 
                            ? (course.description.length > 100 
                                ? `${course.description.substring(0, 100)}...` 
                                : course.description)
                            : <span className="text-muted fst-italic">No description</span>
                          }
                        </div>
                      </td>
                      <td className="text-center">
                        <Badge bg="info">{stats.sectionCount}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="secondary">{stats.contentCount}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="primary">{stats.questionCount}</Badge>
                      </td>
                      <td>
                        <Button 
                          size="sm" 
                          variant="outline-primary" 
                          as={Link}
                          to={`/admin/courses/${course.id}`}
                          className="me-1"
                        >
                          Manage
                        </Button>
                        {userIsAdmin && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline-secondary"
                              onClick={() => handleEditClick(course)}
                              className="me-1"
                            >
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline-danger"
                              onClick={() => handleDelete(course)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {userIsAdmin && (
        <CourseEditorModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSave}
          course={editingCourse}
        />
      )}
    </div>
  );
}
